import React, { useState, useEffect } from 'react';
import { validateFormData, ValidationError, getErrorsForSection } from '../utils/formValidation';
import { FormErrorSummary, SectionErrorSummary } from './ErrorDisplay';
import { getMyProfileCount, getPrefillData } from '../services/recommendationService';
import toast from 'react-hot-toast';

import { FormData, FormDepth } from './advanced-form/types';
import { getFieldPriority, getFieldValue } from './advanced-form/helpers';
import Tab0BasicInfo from './advanced-form/Tab0BasicInfo';
import Tab1SwingCharacteristics from './advanced-form/Tab1SwingCharacteristics';
import Tab2BallFlightData from './advanced-form/Tab2BallFlightData';
import Tab4EquipmentHistory from './advanced-form/Tab4EquipmentHistory';
import Tab6Preferences from './advanced-form/Tab6Preferences';
import Tab7GoalsChallenges from './advanced-form/Tab7GoalsChallenges';

interface AdvancedRecommendationFormProps {
  onSubmit: (profile: FormData) => void;
  loading?: boolean;
  className?: string;
  initialSection?: number | null;
  inReviewMode?: boolean;
  onReturnToReview?: () => void;
  hasPreviousFittings?: boolean;
}

const AdvancedRecommendationForm: React.FC<AdvancedRecommendationFormProps> = ({
  onSubmit,
  loading = false,
  className = "",
  initialSection = null,
  inReviewMode = false,
  onReturnToReview,
  hasPreviousFittings: hasPreviousFittingsProp
}) => {
  const [formData, setFormData] = useState<FormData>({});
  const [currentSection, setCurrentSection] = useState(initialSection ?? 0);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [showValidation, setShowValidation] = useState(false);
  const [showSaveIndicator, setShowSaveIndicator] = useState(false);
  const [hasPreviousFittingsLocal, setHasPreviousFittingsLocal] = useState(false);
  const [usePrefill, setUsePrefill] = useState(false);
  const [prefillSource, setPrefillSource] = useState<string | null>(null);
  const [prefillStrategy, setPrefillStrategy] = useState<'latest' | 'most_complete'>('latest');

  // S4: replaces native window.confirm/alert. hideCancel renders a single-button
  // acknowledgement (the old blocking alert() cases); danger tints the confirm button
  // for destructive actions (clear data / restart form).
  interface ConfirmDialogState {
    title: string;
    message: React.ReactNode;
    confirmLabel?: string;
    cancelLabel?: string;
    hideCancel?: boolean;
    danger?: boolean;
    onConfirm: () => void;
    onCancel?: () => void;
  }
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState | null>(null);

  // F2: form depth selector. Default 'plus' — mandatory + good-to-fill, which is what most
  // users should see. 'essential' is the fast path; 'everything' unlocks Preferences (F3) plus
  // every optional field within each tab.
  const DEPTH_STORAGE_KEY = 'golf-advanced-form-depth';
  const [depth, setDepth] = useState<FormDepth>(() => {
    try {
      const saved = localStorage.getItem(DEPTH_STORAGE_KEY);
      if (saved === 'essential' || saved === 'plus' || saved === 'everything') return saved;
    } catch { /* ignore */ }
    return 'plus';
  });
  useEffect(() => {
    try {
      localStorage.setItem(DEPTH_STORAGE_KEY, depth);
    } catch { /* ignore */ }
  }, [depth]);

  // Auto-save functionality
  const STORAGE_KEY = 'golf-advanced-form-data';
  const SECTION_STORAGE_KEY = 'golf-advanced-form-section';

  // F3: 6 tabs at full depth (down from 8) — Physical Profile folded into Basic Information,
  // Playing Profile folded into Equipment History. Preferences (F2) hides below "Everything".
  const sections = [
    "Basic Information",
    "Swing Characteristics",
    "Ball Flight Data",
    "Equipment History",
    "Preferences",
    "Goals & Challenges"
  ];
  const isSectionVisibleAtDepth = (sectionName: string): boolean =>
    sectionName !== "Preferences" || depth === 'everything';
  const visibleSectionIndices = sections
    .map((_, i) => i)
    .filter(i => isSectionVisibleAtDepth(sections[i]));

  // If the current tab disappears (e.g. depth dropped below "Everything" while on Preferences),
  // snap back to the nearest visible tab instead of rendering a hidden one.
  useEffect(() => {
    if (!isSectionVisibleAtDepth(sections[currentSection])) {
      const fallback = [...visibleSectionIndices].reverse().find(i => i < currentSection) ?? visibleSectionIndices[0] ?? 0;
      setCurrentSection(fallback);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depth]);

  // Load saved data on component mount
  useEffect(() => {
    try {
      const savedData = localStorage.getItem(STORAGE_KEY);
      const savedSection = localStorage.getItem(SECTION_STORAGE_KEY);

      if (savedData) {
        const parsedData = JSON.parse(savedData);
        setFormData(parsedData);
      }

      // Only restore section from localStorage when not navigating from review
      // (initialSection from review takes precedence)
      if (savedSection && initialSection === null) {
        const sectionIndex = parseInt(savedSection, 10);
        if (sectionIndex >= 0 && sectionIndex < sections.length && isSectionVisibleAtDepth(sections[sectionIndex])) {
          setCurrentSection(sectionIndex);
        }
      }
    } catch (error) {
      console.error('Error loading saved form data:', error);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save data whenever formData changes
  useEffect(() => {
    if (Object.keys(formData).length > 0) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
        setShowSaveIndicator(true);

        // Hide save indicator after 2 seconds
        const timer = setTimeout(() => {
          setShowSaveIndicator(false);
        }, 2000);

        return () => clearTimeout(timer);
      } catch (error) {
        console.error('Error saving form data:', error);
      }
    }
  }, [formData]);

  // Save current section whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(SECTION_STORAGE_KEY, currentSection.toString());
    } catch (error) {
      console.error('Error saving current section:', error);
    }
  }, [currentSection]);

  // Derived: prefer prop from App.tsx, fall back to local async check
  const hasPreviousFittings = hasPreviousFittingsProp ?? hasPreviousFittingsLocal;

  // Check if user has previous fittings on component mount (only if prop not provided)
  useEffect(() => {
    if (hasPreviousFittingsProp !== undefined) return; // Prop takes precedence
    const checkPreviousFittings = async () => {
      console.log('🔍 Checking for previous fittings...');
      try {
        const data = await getMyProfileCount();
        console.log('✅ Profile count data:', data);
        console.log(`📊 Total fittings: ${data.total_fittings}`);
        const hasFittings = data.total_fittings > 0;
        console.log(`🎯 Setting hasPreviousFittings to: ${hasFittings}`);
        setHasPreviousFittingsLocal(hasFittings);
      } catch (error) {
        console.error('❌ Failed to check previous fittings:', error);
        // Silent fail - banner won't show, but form still works
      }
    };

    checkPreviousFittings();
  }, [hasPreviousFittingsProp]);

  // Jump to specific section if provided (when editing from review)
  useEffect(() => {
    if (initialSection !== null && initialSection !== undefined) {
      setCurrentSection(initialSection);
    }
  }, [initialSection]);

  // Clear saved data when form is submitted successfully
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const clearSavedData = () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(SECTION_STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing saved data:', error);
    }
  };

  // Handle pre-fill checkbox toggle
  const handlePrefillToggle = async (checked: boolean) => {
    setUsePrefill(checked);

    if (checked) {
      try {
        const data = await getPrefillData(undefined, prefillStrategy);

        // Pre-fill all form fields
        setFormData(data.prefill_data);
        setPrefillSource(data.source_created_at);

        // Show success message with strategy info
        toast.success(data.message, { duration: 5000 });
      } catch (error: any) {
        console.error('Failed to load previous data:', error);
        toast.error(error.message || 'Could not load previous fitting data');
        setUsePrefill(false);
      }
    } else {
      // User unchecked - clear form
      setConfirmDialog({
        title: 'Clear form data?',
        message: 'This will clear all form data.',
        confirmLabel: 'Clear data',
        danger: true,
        onConfirm: () => {
          setFormData({});
          setPrefillSource(null);
          setConfirmDialog(null);
        },
        onCancel: () => setUsePrefill(true), // Keep it checked
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form data
    const validation = validateFormData(formData);
    setValidationErrors(validation.errors);
    setShowValidation(true);

    if (validation.isValid) {
      // DEBUG: Check what Ball Flight Data exists
      console.log('🔍 Ball Flight Data in formData:');
      console.log('  driver_ball_flight:', formData.driver_ball_flight);
      console.log('  iron_data:', formData.iron_data);
      console.log('  wedge_data:', formData.wedge_data);

      // Flatten and restructure data for backend submission
      const submissionData = {
        ...formData,
        // Flatten driver/iron swing data (basic swing characteristics) - backend expects these flat
        ...(formData.driver_swing_data || {}),
        ...(formData.iron_swing_data || {}),
        // Flatten launch monitor data - backend expects these flat
        ...(formData.launch_monitor_driver || {}),
        ...(formData.launch_monitor_iron || {}),
        ...(formData.launch_monitor_wedge || {}),
        // Keep ball flight data nested - backend expects driver_ball_flight, iron_data, wedge_data as objects
        // (these are already nested in formData, no need to change)
        // Remove flattened swing data objects to avoid duplication
        driver_swing_data: undefined,
        iron_swing_data: undefined,
        launch_monitor_driver: undefined,
        launch_monitor_iron: undefined,
        launch_monitor_wedge: undefined
      };

      // DEBUG: Verify Ball Flight Data is in submissionData
      console.log('📤 submissionData Ball Flight:');
      console.log('  driver_ball_flight:', submissionData.driver_ball_flight);
      console.log('  iron_data:', submissionData.iron_data);
      console.log('  wedge_data:', submissionData.wedge_data);

      // Don't clear saved data here - only clear after successful backend submission
      // This allows users to refresh on the review page without losing data
      onSubmit(submissionData);
    } else {
      // Scroll to first error section
      const firstErrorSection = validation.errors[0]?.section;
      if (firstErrorSection) {
        scrollToSection(firstErrorSection);
      }
    }
  };

  const scrollToSection = (sectionName: string) => {
    const sectionIndex = sections.findIndex(section => section === sectionName);
    if (sectionIndex !== -1) {
      setCurrentSection(sectionIndex);
    }
  };

  const updateFormData = (updates: Partial<FormData>) => {
    setFormData(prev => ({ ...prev, ...updates }));

    // Clear validation errors for fields that were just updated if validation is showing
    if (showValidation) {
      const updatedFields = Object.keys(updates);
      setValidationErrors(prev =>
        prev.filter(error => !updatedFields.some(field =>
          error.field.toLowerCase().includes(field.toLowerCase())
        ))
      );
    }
  };

  const validateCurrentSection = () => {
    const validation = validateFormData(formData);
    const currentSectionName = sections[currentSection];
    const currentSectionErrors = getErrorsForSection(validation.errors, currentSectionName);

    // Update validation errors for current section only
    setValidationErrors(prev => {
      const otherSectionErrors = prev.filter(error => error.section !== currentSectionName);
      return [...otherSectionErrors, ...currentSectionErrors];
    });

    return currentSectionErrors.length === 0;
  };

  // Check for missing mandatory and GTF fields in current section
  const checkPriorityFields = () => {
    const currentSectionName = sections[currentSection];
    const missingMandatory: string[] = [];
    const missingGTF: string[] = [];

    // Get section-specific field paths based on current section
    let sectionFieldPaths: string[] = [];

    if (currentSectionName === "Basic Information") {
      // F3: includes the former Physical Profile tab, folded into Basic Information
      sectionFieldPaths = [
        'handicap', 'age', 'height_inches', 'years_playing', 'dominant_hand', 'rounds_per_year',
        'physical_profile.wrist_to_floor', 'physical_profile.flexibility_rating', 'physical_profile.core_strength',
        'physical_profile.weekly_workouts', 'physical_profile.posture_type'
      ];
    } else if (currentSectionName === "Swing Characteristics") {
      // Special handling for Swing Characteristics - check EITHER Manual Entry OR Launch Monitor
      const hasSwingLM = formData.driver_swing_data?.swing_speed_driver !== undefined ||
                         formData.iron_swing_data?.swing_speed_7iron !== undefined;

      if (hasSwingLM) {
        // User is using Launch Monitor - don't require manual swing fields
        sectionFieldPaths = [];
      } else {
        // User is using Manual Entry - require manual swing fields
        sectionFieldPaths = [
          'driver_swing_data.swing_speed_driver', 'driver_swing_data.attack_angle_driver',
          'iron_swing_data.swing_speed_7iron', 'iron_swing_data.attack_angle_irons'
        ];
      }
    } else if (currentSectionName === "Ball Flight Data") {
      sectionFieldPaths = [
        'launch_monitor_driver.launch_angle_driver', 'launch_monitor_driver.spin_rate_driver', 'launch_monitor_driver.ball_speed_driver',
        'launch_monitor_driver.driver_carry_distance', 'launch_monitor_driver.face_to_path_driver',
        'launch_monitor_iron.launch_angle_7i', 'launch_monitor_iron.spin_rate_7i', 'launch_monitor_iron.ball_speed_7i',
        'launch_monitor_iron.iron_carry_distance', 'launch_monitor_iron.face_to_path_7i',
        'launch_monitor_wedge.launch_angle_wedge', 'launch_monitor_wedge.spin_rate_wedge', 'launch_monitor_wedge.wedge_carry_distance'
      ];
    } else if (currentSectionName === "Equipment History") {
      // F3: includes the former Playing Profile tab, folded into Equipment History.
      // Rounds Per Year lives only in Basic Information now (F5 — was duplicated here).
      sectionFieldPaths = [
        'equipment_history.current_driver_satisfaction', 'equipment_history.current_iron_satisfaction', 'equipment_history.current_wedge_satisfaction',
        'equipment_history.current_clubs_driver.brand', 'equipment_history.current_clubs_driver.model', 'equipment_history.current_clubs_driver.loft',
        'equipment_history.current_clubs_driver.shaft_flex', 'equipment_history.current_clubs_driver.shaft_model', 'equipment_history.current_clubs_driver.shaft_weight',
        'equipment_history.current_clubs_irons.brand', 'equipment_history.current_clubs_irons.model', 'equipment_history.current_clubs_irons.shaft_flex',
        'equipment_history.current_clubs_irons.shaft_model', 'equipment_history.current_clubs_irons.shaft_weight',
        'equipment_history.current_clubs_wedges.brand', 'equipment_history.current_clubs_wedges.model', 'equipment_history.current_clubs_wedges.lofts',
        'equipment_history.current_clubs_wedges.grinds', 'equipment_history.current_clubs_wedges.shaft_flex', 'equipment_history.all_time_favorite_club.brand', 'equipment_history.all_time_favorite_club.model',
        'equipment_history.all_time_favorite_club.club_type', 'equipment_history.all_time_favorite_club.shaft_flex',
        'equipment_history.all_time_favorite_club.shaft_model', 'equipment_history.all_time_favorite_club.shaft_weight',
        'equipment_history.equipment_change_frequency', 'equipment_history.budget_flexibility', 'equipment_history.custom_fitting_history',
        'playing_profile.typical_course_type', 'playing_profile.scoring_consistency',
        'playing_profile.performance_under_pressure', 'playing_profile.driver_under_pressure', 'playing_profile.irons_under_pressure', 'playing_profile.wedges_under_pressure'
      ];
    } else if (currentSectionName === "Preferences") {
      sectionFieldPaths = [
        'preferences.feel_preference', 'preferences.sound_preference', 'preferences.visual_alignment_aids', 'preferences.offset_preference',
        'preferences.Materials_you_are_interested_in_irons', 'preferences.Materials_you_are_interested_in_drivers',
        'preferences.Shiftable_Lofts_drivers', 'preferences.Bendable_irons'
      ];
    } else if (currentSectionName === "Goals & Challenges") {
      sectionFieldPaths = [
        'goals_challenges.primary_goal', 'goals_challenges.target_handicap', 'goals_challenges.biggest_frustration',
        'goals_challenges.purchase_timeline', 'goals_challenges.practice_frequency', 'goals_challenges.lesson_frequency'
      ];
    }

    // Check each field for missing values
    sectionFieldPaths.forEach(fieldPath => {
      const priority = getFieldPriority(fieldPath);
      const value = getFieldValue(formData, fieldPath);

      // Check if value is actually empty (null/undefined/empty string)
      // Note: 0 is a valid value for numeric fields like launch angle, spin rate, etc.
      const isEmpty = value === null || value === undefined || (typeof value === 'string' && value.trim() === '');

      if (isEmpty) {
        if (priority === 'M') {
          missingMandatory.push(fieldPath);
        } else if (priority === 'GTF') {
          missingGTF.push(fieldPath);
        }
      }
    });

    return { missingMandatory, missingGTF };
  };

  const updateNestedField = <T extends keyof FormData>(
    section: T,
    updates: Partial<FormData[T]>
  ) => {
    setFormData(prev => ({
      ...prev,
      [section]: { ...(prev[section] as any), ...updates }
    }));
  };

  const handleRestartForm = () => {
    setConfirmDialog({
      title: 'Restart the form?',
      message: "This clears all your saved form data. You'll start again from the beginning.",
      confirmLabel: 'Restart form',
      danger: true,
      onConfirm: () => {
        try {
          localStorage.removeItem(STORAGE_KEY);
          localStorage.removeItem(SECTION_STORAGE_KEY);
          setFormData({});
          setCurrentSection(0);
          setValidationErrors([]);
          toast.success('Form restarted. All data cleared.');
        } catch (error) {
          console.error('Error clearing form data:', error);
          toast.error('Could not clear form data. Try refreshing the page.');
        }
        setConfirmDialog(null);
      },
    });
  };

  const tabProps = {
    formData,
    updateFormData,
    updateNestedField,
    showValidation,
    validationErrors,
    depth,
  };

  const renderSection = () => {
    switch (currentSection) {
      case 0: return <Tab0BasicInfo {...tabProps} />;
      case 1: return <Tab1SwingCharacteristics {...tabProps} />;
      case 2: return <Tab2BallFlightData {...tabProps} />;
      case 3: return <Tab4EquipmentHistory {...tabProps} />;
      case 4: return <Tab6Preferences {...tabProps} />;
      case 5: return <Tab7GoalsChallenges {...tabProps} />;
      default:
        return (
          <div className="text-center py-8">
            <div className="text-blue-600">
              <h3 className="text-lg font-semibold mb-2">Coming Soon</h3>
              <p>Additional professional fitting sections are being implemented.</p>
            </div>
          </div>
        );
    }
  };

  // Debug: Log state at render time
  console.log(`📝 Rendering form - hasPreviousFittings: ${hasPreviousFittings}, usePrefill: ${usePrefill}`);

  return (
    <div className={`max-w-4xl mx-auto ${className}`}>
      {/* Pre-fill Banner */}
      {hasPreviousFittings && (
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6 rounded-r-lg">
          {usePrefill && prefillSource ? (
            /* Confirmation state: prefill has been loaded */
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <span className="text-sm font-semibold text-blue-900">
                  Pre-filled from fitting on {new Date(prefillSource).toLocaleDateString()}
                </span>
                <p className="text-sm text-blue-700 mt-1">
                  Update any fields below before submitting.
                </p>
              </div>
              <button
                type="button"
                onClick={() => handlePrefillToggle(false)}
                className="self-start sm:self-auto text-sm text-blue-600 underline hover:text-blue-800 whitespace-nowrap"
              >
                Clear &amp; Start Fresh
              </button>
            </div>
          ) : (
            /* Selection state: pick strategy then click button */
            <div>
              <p className="text-sm font-semibold text-blue-900">
                💾 Start from a previous fitting?
              </p>
              <p className="text-sm text-blue-700 mt-1">
                Saves time — update only what's changed.
              </p>
              <div className="mt-3 space-y-2">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="prefillStrategy"
                    value="latest"
                    checked={prefillStrategy === 'latest'}
                    onChange={() => setPrefillStrategy('latest')}
                    className="h-4 w-4 text-blue-600 border-blue-300 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-blue-800">
                    <strong>Most Recent</strong> - Your latest fitting by date
                  </span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="prefillStrategy"
                    value="most_complete"
                    checked={prefillStrategy === 'most_complete'}
                    onChange={() => setPrefillStrategy('most_complete')}
                    className="h-4 w-4 text-blue-600 border-blue-300 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-blue-800">
                    <strong>Most Complete</strong> - Fitting with most data filled
                  </span>
                </label>
              </div>
              <button
                type="button"
                onClick={() => handlePrefillToggle(true)}
                className="mt-3 bg-green-ink hover:opacity-90 text-white text-sm font-medium px-4 py-2 rounded-md transition-colors"
              >
                Pre-fill from Previous Fitting
              </button>
            </div>
          )}
        </div>
      )}

      {/* Progress Bar */}
      {/* F2: Form depth selector — controls how many fields/tabs are shown */}
      <div className="mb-6 bg-paper border border-line rounded-brand p-3">
        <p className="text-sm font-medium text-ink-2 mb-2">How much do you want to fill in?</p>
        <div className="flex flex-wrap gap-2">
          {([
            { key: 'essential', label: 'Essential', desc: 'Just what recommendations require' },
            { key: 'plus', label: '+ Good-to-have', desc: 'Sharper recommendations (default)' },
            { key: 'everything', label: 'Everything', desc: 'Full fitting profile, incl. Preferences' },
          ] as const).map(opt => (
            <button
              key={opt.key}
              type="button"
              onClick={() => setDepth(opt.key)}
              className={`flex-1 min-w-[9rem] text-left px-3 py-2 rounded-md border text-sm transition-colors ${
                depth === opt.key
                  ? 'bg-green-ink border-green-ink text-white'
                  : 'bg-paper border-line-2 text-ink-2 hover:border-green-ink'
              }`}
            >
              <div className="font-semibold">{opt.label}</div>
              <div className={`text-xs mt-0.5 ${depth === opt.key ? 'text-green-ink' : 'text-ink-3'}`}>{opt.desc}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="mb-8">
        <div className="flex justify-between items-center mb-2">
          <span className="text-base font-medium text-ink-2">Progress</span>
          <div className="flex items-center gap-3">
            {showSaveIndicator && (
              <span className="text-sm text-green-ink bg-green/15 px-2 py-1 rounded">
                ✓ Auto-saved
              </span>
            )}
            <button
              onClick={handleRestartForm}
              className="text-sm text-white bg-score-0 hover:opacity-90 px-3 py-1 min-h-[44px] rounded-md font-medium transition-colors"
              type="button"
            >
              Restart form
            </button>
            <span className="text-sm text-ink-3">
              {visibleSectionIndices.indexOf(currentSection) + 1} of {visibleSectionIndices.length}
            </span>
          </div>
        </div>
        <div className="w-full bg-line rounded-full h-2">
          <div
            className="bg-green-ink h-2 rounded-full transition-all duration-300"
            style={{ width: `${((visibleSectionIndices.indexOf(currentSection) + 1) / visibleSectionIndices.length) * 100}%` }}
          ></div>
        </div>
      </div>


      {/* Form-wide Error Summary */}
      {showValidation && validationErrors.length > 0 && (
        <FormErrorSummary
          errors={validationErrors}
          onScrollToSection={scrollToSection}
        />
      )}

      {/* Section Navigation */}
      <div className="mb-6">
        {/* Mobile: Dropdown/Stepper */}
        <div className="block md:hidden">
          <div className="flex items-center justify-between mb-4">
            <select
              value={currentSection}
              onChange={(e) => setCurrentSection(parseInt(e.target.value))}
              className="text-base px-4 py-3 min-h-[44px] rounded-md border-line-2 shadow-raised focus:border-green-ink focus:ring-green-ink"
            >
              {visibleSectionIndices.map((index, i) => (
                <option key={index} value={index}>
                  {i + 1}. {sections[index]}
                </option>
              ))}
            </select>
            <span className="text-sm text-ink-3 ml-4">
              {visibleSectionIndices.indexOf(currentSection) + 1} of {visibleSectionIndices.length}
            </span>
          </div>

          {/* Mobile Priority Guide */}
          <div className="text-center text-xs text-ink-3 mb-4 space-y-1">
            <div className="flex items-center justify-center gap-2">
              <span className="text-score-0 font-bold">*</span>
              <span>Mandatory</span>
              <span className="text-score-1 font-bold ml-3">●</span>
              <span>Good to Fill</span>
            </div>
          </div>
        </div>

        {/* Desktop: Horizontal Tabs */}
        <div className="hidden md:block">
          <div className="flex flex-wrap gap-2">
            {visibleSectionIndices.map((index) => {
              const section = sections[index];
              const sectionErrors = getErrorsForSection(validationErrors, section);
              const hasErrors = showValidation && sectionErrors.length > 0;

              return (
                <button
                  key={index}
                  onClick={() => setCurrentSection(index)}
                  className={`px-3 py-2 text-sm rounded-md relative min-h-[44px] ${
                    currentSection === index
                      ? hasErrors
                        ? 'bg-score-0 text-white'
                        : 'bg-green-ink text-white'
                      : hasErrors
                        ? 'bg-score-0-bg text-score-0 hover:opacity-90'
                        : 'bg-line text-ink-2 hover:bg-line-2'
                  }`}
                >
                  {section}
                  {hasErrors && (
                    <span className="absolute -top-1 -right-1 bg-score-0 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {sectionErrors.length}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Priority Guide - Second Row */}
          <div className="mt-2 flex gap-6 text-xs text-ink-3">
            <div className="flex items-center gap-1">
              <span className="text-score-0 font-bold">*</span>
              <span>Mandatory - Required for recommendations</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-score-1 font-bold">●</span>
              <span>Good to Fill - Significantly improves recommendations</span>
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Current Section Content */}
        <div className="bg-paper p-6 rounded-brand shadow-raised border border-line">
          {/* Section Error Summary */}
          {showValidation && (
            <SectionErrorSummary
              errors={validationErrors}
              section={sections[currentSection]}
            />
          )}

          {renderSection()}
        </div>

        {/* Navigation Buttons */}
        {(() => {
          const posInVisible = visibleSectionIndices.indexOf(currentSection);
          const isFirstVisible = posInVisible <= 0;
          const isLastVisible = posInVisible >= visibleSectionIndices.length - 1;
          const goToVisible = (delta: number) => {
            const target = visibleSectionIndices[posInVisible + delta];
            if (target !== undefined) setCurrentSection(target);
          };
          return (
        <div className="flex flex-col-reverse md:flex-row justify-between gap-4">
          <button
            type="button"
            onClick={() => goToVisible(-1)}
            disabled={isFirstVisible}
            className={`w-full md:w-auto px-6 py-3 text-base min-h-[44px] rounded-md ${
              isFirstVisible
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-gray-600 text-white hover:bg-gray-700'
            }`}
          >
            Previous
          </button>

          <div className="flex space-x-4">
            {inReviewMode && onReturnToReview && (
              <button
                type="button"
                onClick={onReturnToReview}
                className="px-6 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 font-semibold"
              >
                ← Return to Review
              </button>
            )}
            {!isLastVisible ? (
              <button
                type="button"
                onClick={() => {
                  // Check for missing priority fields
                  const { missingMandatory, missingGTF } = checkPriorityFields();

                  if (missingMandatory.length > 0) {
                    // Block navigation if mandatory fields are missing
                    setConfirmDialog({
                      title: 'Fill in mandatory fields first',
                      message: (
                        <>
                          <p className="mb-2">These fields are required before you can continue (marked with a red asterisk):</p>
                          <ul className="list-disc pl-5 space-y-0.5">
                            {missingMandatory.map(field => <li key={field}>{field.split('.').pop()}</li>)}
                          </ul>
                        </>
                      ),
                      confirmLabel: 'Got it',
                      hideCancel: true,
                      onConfirm: () => setConfirmDialog(null),
                    });
                    setShowValidation(true);
                    return;
                  }

                  if (missingGTF.length > 0) {
                    // Warn about missing GTF fields but allow navigation
                    setConfirmDialog({
                      title: 'Some recommended fields are empty',
                      message: (
                        <>
                          <p className="mb-2">These fields significantly improve recommendation accuracy (marked with ●):</p>
                          <ul className="list-disc pl-5 space-y-0.5">
                            {missingGTF.map(field => <li key={field}>{field.split('.').pop()}</li>)}
                          </ul>
                        </>
                      ),
                      confirmLabel: 'Continue anyway',
                      cancelLabel: 'Go back',
                      onConfirm: () => {
                        setConfirmDialog(null);
                        setShowValidation(true);
                        if (validateCurrentSection()) goToVisible(1);
                      },
                      onCancel: () => setShowValidation(true),
                    });
                    return;
                  }

                  // Validate current section before proceeding
                  setShowValidation(true);
                  const isValid = validateCurrentSection();
                  if (isValid) {
                    goToVisible(1);
                  }
                }}
                className="w-full md:w-auto px-6 py-3 text-base min-h-[44px] bg-green-ink text-white rounded-md hover:opacity-90"
              >
                Next
              </button>
            ) : (
              <button
                type="submit"
                disabled={loading}
                className={`w-full md:w-auto px-8 py-3 text-base min-h-[44px] rounded-md text-white font-semibold ${
                  loading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-green-ink hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-green-ink focus:ring-offset-2'
                }`}
              >
                {loading ? 'Analyzing...' : 'Get Professional Recommendations'}
              </button>
            )}
          </div>
        </div>
          );
        })()}
      </form>

      {confirmDialog && (
        <div className="fixed inset-0 bg-ink/40 flex items-center justify-center z-50 p-4">
          <div className="bg-paper rounded-brand-lg shadow-overlay border border-line max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-ink mb-2">{confirmDialog.title}</h3>
            <div className="text-sm text-ink-2 mb-6">{confirmDialog.message}</div>
            <div className="flex justify-end gap-3">
              {!confirmDialog.hideCancel && (
                <button
                  type="button"
                  onClick={() => {
                    confirmDialog.onCancel?.();
                    setConfirmDialog(null);
                  }}
                  className="px-4 py-2 text-sm min-h-[44px] rounded-md border border-line text-ink-2 hover:bg-line/40"
                >
                  {confirmDialog.cancelLabel || 'Cancel'}
                </button>
              )}
              <button
                type="button"
                onClick={confirmDialog.onConfirm}
                className={`px-4 py-2 text-sm min-h-[44px] rounded-md text-white hover:opacity-90 ${
                  confirmDialog.danger ? 'bg-score-0' : 'bg-green-ink'
                }`}
              >
                {confirmDialog.confirmLabel || 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedRecommendationForm;
